import { playCross } from './effects/cross'
import { primeSounds, trigger } from './engine'

/**
 * Global, non-search easter egg triggers that live on every page (imported
 * from assets/frontend/main.ts). Unlike the search eggs these fire from a
 * user gesture rather than a query, so they register plain DOM listeners and
 * call trigger() by slug once the gesture is recognised.
 *
 * The Konami code has a mobile counterpart: vigorously shaking the device
 * fires the very same 'konami' egg (see initShake below).
 */

/**
 * The classic Konami code. Fires a full-screen "worship moment" (see the
 * 'konami' egg in eggs.ts). A rolling buffer of the last keys is compared to
 * the sequence, so it works no matter what was typed before.
 */
function initKonami() {
  const sequence = [
    'arrowup',
    'arrowup',
    'arrowdown',
    'arrowdown',
    'arrowleft',
    'arrowright',
    'arrowleft',
    'arrowright',
    'b',
    'a'
  ]
  const buffer: string[] = []

  window.addEventListener('keydown', (event) => {
    // ignore modifier-driven shortcuts so real key combos never spoil the run
    if (event.ctrlKey || event.metaKey || event.altKey) return

    buffer.push(event.key.toLowerCase())
    if (buffer.length > sequence.length) buffer.shift()

    if (
      buffer.length === sequence.length &&
      sequence.every((key, index) => buffer[index] === key)
    ) {
      buffer.length = 0
      trigger('konami', 'konami')
    }
  })
}

/**
 * Mobile counterpart to the Konami code: a few firm shakes of the device fire
 * the same 'konami' egg. We watch DeviceMotion's acceleration (gravity already
 * excluded) and count "jolts" - moments where the total acceleration crosses a
 * threshold. Enough jolts within a rolling window trip the egg.
 *
 * Shake is armed only after a secret warm-up: the user has to tap the footer
 * logo 7 times. On the 7th tap we request DeviceMotion access and start
 * listening. This keeps the (loud) iOS 13+ permission prompt - which may only be
 * requested from a user gesture - from ever appearing on a plain page visit; it
 * only shows once someone has deliberately poked the logo. Browsers without the
 * permission gate (Android, older iOS) simply start listening on that 7th tap.
 * The desktop keyboard Konami code is unaffected and still fires directly.
 */
function initShake() {
  if (typeof window.DeviceMotionEvent === 'undefined') return

  const joltThreshold = 16 // m/s^2 of extra acceleration to count as a jolt
  const joltsNeeded = 5 // jolts required to fire
  const windowMs = 1500 // rolling window the jolts must fall within
  const minGapMs = 120 // debounce between jolts so one shake isn't double-counted

  const tapsNeeded = 7 // footer-logo taps that arm the shake detector
  const tapResetMs = 2000 // taps must follow one another within this gap
  // Once the taps are done we remember it for the rest of the browser session
  // (a plain true/false flag, never the running tap count) so the user does not
  // have to tap the logo 7 times again on every page.
  // on the engine's reset prefix (see storagePrefixes), so reset() clears it
  const armedKey = 'icf-easter-egg-shake-armed'

  const jolts: number[] = []
  let lastJolt = 0

  function onMotion(event: DeviceMotionEvent) {
    const acc = event.acceleration
    if (!acc) return

    const magnitude = Math.hypot(acc.x ?? 0, acc.y ?? 0, acc.z ?? 0)
    if (magnitude < joltThreshold) return

    const now = Date.now()
    if (now - lastJolt < minGapMs) return
    lastJolt = now

    jolts.push(now)
    while (jolts.length && now - (jolts[0] ?? now) > windowMs) jolts.shift()

    if (jolts.length >= joltsNeeded) {
      jolts.length = 0
      trigger('konami', 'shake')
    }
  }

  function startListening() {
    window.addEventListener('devicemotion', onMotion)
  }

  const requestPermission = (
    window.DeviceMotionEvent as unknown as {
      requestPermission?: () => Promise<'granted' | 'denied'>
    }
  ).requestPermission

  function wasArmedThisSession() {
    try {
      return window.sessionStorage.getItem(armedKey) === 'true'
    } catch {
      return false
    }
  }

  function rememberArmed() {
    try {
      window.sessionStorage.setItem(armedKey, 'true')
    } catch {
      // storage unavailable (private mode etc.) - just skip the shortcut
    }
  }

  // Arm the detector - request permission (iOS) or just start listening - once
  // the warm-up taps have been reached. Called from within the tap gesture so
  // the iOS permission prompt is allowed.
  function arm() {
    // still inside the logo-tap gesture: unlock the shake clip so a later
    // shake (not a gesture) can actually play it on iOS - see primeSounds
    primeSounds()

    if (typeof requestPermission === 'function') {
      requestPermission()
        .then((state) => {
          if (state === 'granted') startListening()
        })
        .catch(() => {
          // permission denied or dismissed - shake egg simply stays dormant
        })
    } else {
      startListening()
    }
  }

  // Already earned it earlier this session: skip the taps and just listen.
  // (iOS keeps the granted DeviceMotion permission for the session, so no
  // re-prompt is needed.)
  if (wasArmedThisSession()) {
    startListening()
    return
  }

  // Wire up the "tap the footer logo 7 times" warm-up. Both footer logos share
  // the .brand-image class; on mobile only the visible one can actually be
  // tapped, which is exactly the one we want.
  const logos = document.querySelectorAll<HTMLElement>('.brand-image')
  if (!logos.length) return

  let taps = 0
  let lastTap = 0
  let armed = false

  const onLogoTap = () => {
    if (armed) return
    const now = Date.now()
    if (now - lastTap > tapResetMs) taps = 0
    lastTap = now
    taps += 1

    if (taps >= tapsNeeded) {
      armed = true
      rememberArmed()
      logos.forEach((logo) => logo.removeEventListener('click', onLogoTap))
      arm()
    }
  }

  logos.forEach((logo) => logo.addEventListener('click', onLogoTap))
}

type Point = { x: number; y: number }
type VBar = { x: number; top: number; bottom: number; at: number }
type HBar = { y: number; left: number; right: number; at: number }

// A cross must be drawn at a real size - the reveal lands exactly on the drawn
// beam (same position, same size), so a tiny stray crossing never counts.
// These are the minimum on-screen extents of the two strokes, shared by the
// mouse and touch paths.
const minBeamHeightPx = 130 // vertical beam, top to bottom
const minCrossbarWidthPx = 55 // horizontal crossbar, end to end

// The proportions of the cross that lights up (effects/cross.ts): the crossbar
// is about half the beam's height and sits ~28% down from the top. To force a
// drawing that matches what appears, the detector accepts only crosses near
// these figures (with generous but not unlimited slack).
const crossbarRatioMin = 0.4 // crossbar width / beam height
const crossbarRatioMax = 0.65
const crossbarPositionMin = 0.18 // crossbar centre, as a fraction down the beam
const crossbarPositionMax = 0.4
const armBalanceMin = 0.35 // each crossbar arm as a fraction of the full width

/**
 * Turn a stroke's bounding box into a vertical or horizontal bar, or null if it
 * is too short or too diagonal to be either. Shared by both cross detectors so
 * a "stroke" means the same thing for mouse paths and touch swipes.
 */
function classifyStroke(
  box: { minX: number; maxX: number; minY: number; maxY: number },
  minLengthPx: number,
  straightRatio: number
): { vertical: VBar } | { horizontal: HBar } | null {
  const dx = box.maxX - box.minX
  const dy = box.maxY - box.minY
  const at = Date.now()
  if (dy >= minLengthPx && dx <= dy * straightRatio) {
    return {
      vertical: { x: (box.minX + box.maxX) / 2, top: box.minY, bottom: box.maxY, at }
    }
  }
  if (dx >= minLengthPx && dy <= dx * straightRatio) {
    return {
      horizontal: {
        y: (box.minY + box.maxY) / 2,
        left: box.minX,
        right: box.maxX,
        at
      }
    }
  }
  return null
}

/**
 * Does a captured vertical stroke and horizontal stroke form a cross that
 * closely matches the one that will light up? Returns the beam geometry (centre
 * x, top, height) to draw the reveal right on top of it, or null. The rules are
 * shared by the mouse and touch paths and are deliberately close to the target
 * cross's proportions - you have to draw roughly the cross you get:
 *  - SIZE: both strokes must be drawn big (>= the minimums above).
 *  - PROPORTION: the crossbar must be ~0.4-0.65x the beam's height (the target
 *    is ~0.5), so a flat plus or a bare line is rejected.
 *  - CENTRED ARMS: the crossbar must straddle the beam with balanced arms on
 *    each side, so an "L"/corner or a lopsided bar is not a cross.
 *  - UPRIGHT & HIGH: the crossbar must sit ~0.18-0.4 down the beam (target
 *    ~0.28). A low crossbar (inverted cross) or a top one ("T") is rejected.
 */
function crossHit(
  v: VBar,
  h: HBar,
  pairWindowMs: number
): { x: number; top: number; height: number } | null {
  if (Math.abs(v.at - h.at) > pairWindowMs) return null

  const beamHeight = v.bottom - v.top
  const crossbarWidth = h.right - h.left
  // size gate: the cross must be drawn big, not a tiny scribbled crossing
  if (beamHeight < minBeamHeightPx || crossbarWidth < minCrossbarWidthPx) {
    return null
  }

  // proportion: crossbar width relative to beam height must be cross-like
  const ratio = crossbarWidth / beamHeight
  if (ratio < crossbarRatioMin || ratio > crossbarRatioMax) return null

  // centred arms: the beam must straddle the crossbar with balanced arms
  const leftArm = v.x - h.left
  const rightArm = h.right - v.x
  if (
    leftArm < crossbarWidth * armBalanceMin ||
    rightArm < crossbarWidth * armBalanceMin
  ) {
    return null
  }

  // upright & high: crossbar near the upper third of the beam (0 = top)
  const position = (h.y - v.top) / beamHeight
  if (position < crossbarPositionMin || position > crossbarPositionMax) {
    return null
  }

  return { x: v.x, top: v.top, height: beamHeight }
}

/**
 * Draw-a-cross gesture. The user draws a cross freehand - one vertical stroke
 * and one horizontal stroke - and a glowing cross lights up where they drew it.
 *
 * Listeners are passive and never call preventDefault, so normal clicking,
 * scrolling and text selection are untouched; we merely observe pointer paths
 * and look for two strokes that together form an UPRIGHT cross (see crossHit).
 *
 * This is the mouse/pen path: a real freehand cross, revealed where it was
 * drawn. Touch pointers are ignored here because a vertical drag is claimed by
 * scrolling (pointercancel fires before a stroke completes). Touch has its own
 * detector, initMobileCross, which recognises the same cross shape from swipes.
 */
function initDrawCross() {
  // a stroke must be at least crossbar-length to count; crossHit then enforces
  // the full per-axis size gate (beam >= minBeamHeightPx, etc.)
  const minLengthPx = minCrossbarWidthPx // ignore taps and small drags
  const straightRatio = 0.45 // minor axis must stay under this share of the major
  const pairWindowMs = 3000 // the two strokes must be drawn close in time
  const cooldownMs = 1200 // pause after a hit so one cross fires once

  let points: Point[] = []
  let drawing = false
  let lastVertical: VBar | null = null
  let lastHorizontal: HBar | null = null
  let lockedUntil = 0

  function classify(path: Point[]) {
    if (path.length < 4) return
    let minX = Infinity
    let maxX = -Infinity
    let minY = Infinity
    let maxY = -Infinity
    for (const p of path) {
      if (p.x < minX) minX = p.x
      if (p.x > maxX) maxX = p.x
      if (p.y < minY) minY = p.y
      if (p.y > maxY) maxY = p.y
    }

    const stroke = classifyStroke(
      { minX, maxX, minY, maxY },
      minLengthPx,
      straightRatio
    )
    if (!stroke) return
    if ('vertical' in stroke) lastVertical = stroke.vertical
    else lastHorizontal = stroke.horizontal

    if (!lastVertical || !lastHorizontal) return
    const hit = crossHit(lastVertical, lastHorizontal, pairWindowMs)
    if (!hit) return

    lockedUntil = Date.now() + cooldownMs
    lastVertical = null
    lastHorizontal = null
    playCross({ x: hit.x, top: hit.top, height: hit.height })
    trigger('draw-cross', 'draw-cross')
  }

  window.addEventListener('pointerdown', (event) => {
    // touch drags are owned by scrolling - see the note above
    if (event.pointerType === 'touch') return
    if (Date.now() < lockedUntil) return
    drawing = true
    points = [{ x: event.clientX, y: event.clientY }]
  })
  window.addEventListener(
    'pointermove',
    (event) => {
      if (!drawing) return
      points.push({ x: event.clientX, y: event.clientY })
    },
    { passive: true }
  )
  const finish = () => {
    if (!drawing) return
    drawing = false
    classify(points)
    points = []
  }
  window.addEventListener('pointerup', finish)
  window.addEventListener('pointercancel', finish)
}

/**
 * Touch counterpart to initDrawCross. A freehand cross is impossible on touch
 * (a vertical drag is the scroll gesture), so the cross is drawn as two swipes:
 * a vertical swipe/scroll and a horizontal swipe. Crucially these are not just
 * "any scroll + any swipe" - each swipe's real path is captured (touchmove
 * keeps firing throughout a scroll) and fed to the SAME crossHit test as the
 * mouse. So the two swipes must actually form a cross - matching the target's
 * proportions and position closely - and the reveal then lands right on the
 * swiped beam, same as on the desktop; a stray scroll and an unrelated swipe,
 * or an inverted arrangement, do not count.
 *
 * We use TouchEvents, not PointerEvents: touchmove keeps firing throughout a
 * scroll, whereas pointer events get cancelled the moment scrolling begins.
 * Every listener is passive and never calls preventDefault, so scrolling stays
 * completely untouched.
 */
function initMobileCross() {
  // a swipe must be at least crossbar-length to count; crossHit then enforces
  // the full per-axis size gate (beam >= minBeamHeightPx, etc.)
  const minLengthPx = minCrossbarWidthPx // ignore taps and small drags
  const straightRatio = 0.6 // keep each swipe clearly vertical or horizontal
  const pairWindowMs = 2500 // the two swipes must follow one another closely
  const cooldownMs = 1500 // pause after a hit so one cross fires once

  let tracking = false
  let minX = 0
  let maxX = 0
  let minY = 0
  let maxY = 0
  let lastVertical: VBar | null = null
  let lastHorizontal: HBar | null = null
  let lockedUntil = 0

  window.addEventListener(
    'touchstart',
    (event) => {
      // multi-touch (pinch/zoom) is never a cross
      if (event.touches.length !== 1 || Date.now() < lockedUntil) {
        tracking = false
        return
      }
      const touch = event.touches[0]
      if (!touch) return
      tracking = true
      minX = maxX = touch.clientX
      minY = maxY = touch.clientY
    },
    { passive: true }
  )

  window.addEventListener(
    'touchmove',
    (event) => {
      if (!tracking) return
      if (event.touches.length !== 1) {
        tracking = false
        return
      }
      const touch = event.touches[0]
      if (!touch) return
      if (touch.clientX < minX) minX = touch.clientX
      if (touch.clientX > maxX) maxX = touch.clientX
      if (touch.clientY < minY) minY = touch.clientY
      if (touch.clientY > maxY) maxY = touch.clientY
    },
    { passive: true }
  )

  window.addEventListener(
    'touchend',
    () => {
      if (!tracking) return
      tracking = false

      const stroke = classifyStroke(
        { minX, maxX, minY, maxY },
        minLengthPx,
        straightRatio
      )
      if (!stroke) return
      if ('vertical' in stroke) lastVertical = stroke.vertical
      else lastHorizontal = stroke.horizontal

      if (!lastVertical || !lastHorizontal) return
      const hit = crossHit(lastVertical, lastHorizontal, pairWindowMs)
      if (!hit) return

      lastVertical = null
      lastHorizontal = null
      lockedUntil = Date.now() + cooldownMs
      playCross({ x: hit.x, top: hit.top, height: hit.height, durationMs: 3000 })
      trigger('draw-cross', 'draw-cross')
    },
    { passive: true }
  )
}

initKonami()
initShake()
initDrawCross()
initMobileCross()
